// services/PinSizeService.js

import { deepClean, encodeQueryData } from "@/utils/commonUtils";
import httpService from "./httpService";

const SESSION_KEY = "__nominatim_cache__";

const readCache = () => {
    try { return JSON.parse(sessionStorage.getItem(SESSION_KEY)) || {}; } catch { return {}; }
};

const writeCache = (store) => {
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(store)); } catch {}
};

// Throttle: max 1 request/sec to Nominatim (shared across all methods)
let _nextAllowedTime = 0;
const nominatimThrottle = () => {
    const now = Date.now();
    const wait = Math.max(0, _nextAllowedTime - now);
    _nextAllowedTime = Math.max(now, _nextAllowedTime) + 1100;
    return wait > 0 ? new Promise((r) => setTimeout(r, wait)) : Promise.resolve();
};

// Stable cache key: sort params alphabetically so key is order-independent
const stableKey = (baseUrl, params) => {
    const sorted = params.split("&").sort().join("&");
    return baseUrl + "?" + sorted;
};

// Returns cached response or throttles → fetches → caches { type, url, params, response }
const nominatimGet = async (type, baseUrl, params) => {
    const store = readCache();
    const key = stableKey(baseUrl, params);

    if (store[key]) return store[key].response;

    await nominatimThrottle();
    let result = await httpService.get(baseUrl + "?" + params);

    // 425 Too Early: wait 1.5s and retry once
    if (result?.status_code === 425) {
        await new Promise((r) => setTimeout(r, 1500));
        result = await httpService.get(baseUrl + "?" + params);
    }

    if (result?.success !== false) {
        store[key] = { type, url: baseUrl, params, response: result };
        writeCache(store);
    }
    return result;
};

export default {
    search: async (props) => {
        const { addressdetails = 1, ...rest } = props || {};
        const params = encodeQueryData(deepClean({
            "accept-language": "en",
            format: 'json',
            addressdetails,
            limit: 10,
            ...rest
        }));
        return await nominatimGet('search', 'https://nominatim.openstreetmap.org/search', params);
    },
    reverse: async (props) => {
        const params = encodeQueryData(deepClean({
            "accept-language": "en",
            format: 'json',
            addressdetails: 1,
            ...(props || {})
        }));
        return await nominatimGet('reverse', 'https://nominatim.openstreetmap.org/reverse', params);
    },
    details: async (props) => {
        const placeId = props?.place_id || props?.placeId || "";
        const placeIds = props?.place_ids || props?.placeIds || "";
        const params = encodeQueryData(deepClean({
            "accept-language": "en",
            format: "json",
            addressdetails: 1,
            place_ids: placeIds || (placeId ? String(placeId) : undefined),
        }));
        // Lookup returns array of objects with lat/lon/boundingbox.
        return await nominatimGet('lookup', 'https://nominatim.openstreetmap.org/lookup', params);
    },
}
